import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash-es";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "../../shared/api/index";
import { useNotification } from "~hooks/useNotification";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";

export const useFetchTeam = () => {
  const { fetchData } = useApi();
  const { organizationId, sport, entityId } = useParams();

  return useQuery({
    queryKey: ["entity", organizationId, entityId],
    queryFn: async () => {
      const result = await fetchData({
        url: `${sport}/o/${organizationId}/entities/${entityId}`,
      });

      return result?.data?.[0];
    },
    enabled: !!entityId,
  });
};

export const useFetchSeasonTeams = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport, seasonId } = useParams();
  let URL = `${sport}/o/${organizationId}/seasons/${seasonId}/entities`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["entities", organizationId, seasonId, JSON.stringify(params)],
    queryFn: async () => {
      return await fetchData({
        url: URL,
      });
    },
  });
};

export const useCreateTeam = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  
  return useMutation({
    mutationFn: async (data) => {
      return await mutateData({
        url: `${sport}/o/${organizationId}/entities`,
        method: "POST",
        data,
      });
    },
    onSuccess: async () => {
      notification.success({
        message: formatMessage({
          id: "team.create.success",
          defaultMessage: "Team created successfully",
        }),
        placement: "bottomRight",
      });

      await Promise.all([
        invalidateCache(),
        queryClient.invalidateQueries({ queryKey: ["entities"] }),
      ]);
    },
  });
};

export const useUpdateTeam = () => {
  const { sport, organizationId, entityId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  
  return useMutation({
    mutationFn: async (data) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/entities/${entityId}`,
        method: "PUT",
        data,
      });
    },
    onSuccess: async () => {
      notification.success({
        message: formatMessage({
          id: "team.update.success",
          defaultMessage: "Team updated successfully",
        }),
        placement: "bottomRight",
      });
      
      await Promise.all([
        invalidateCache(),
        queryClient.invalidateQueries({ queryKey: ["entities"] }),
      ]);
    },
  });
};

export const useSubmitTeamToSeason = () => {
  const { sport, organizationId, seasonId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  
  return useMutation({
    mutationFn: async (data) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/seasons/${seasonId}/entities`,
        method: "POST",
        data,
      });
    },
    onSuccess: async () => {
      notification.success({
        message: "Successfully submitted team",
        placement: "bottomRight",
      });

      await Promise.all([
        invalidateCache(),
        queryClient.invalidateQueries({ queryKey: ["entities"] }),
      ]);
    },
  });
};

export const useDeleteTeamFromSeason = () => {
  const { sport, organizationId, seasonId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  
  return useMutation({
    mutationFn: async (entityId) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/seasons/${seasonId}/entities/${entityId}`,
        method: "DELETE",
      });
    },
    onSuccess: async () => {
      notification.success({
        message: formatMessage({
          id: "team.delete.success",
          defaultMessage: "Team deleted successfully",
        }),
        placement: "bottomRight",
      });
      
      await Promise.all([
        invalidateCache(),
        queryClient.invalidateQueries({ queryKey: ["entities"] }),
      ]);
    },
  });
};
